import { BalanceQuery, EripTopUpQuery } from "@/__generated__/graphql";
import { ContentCopy, Done } from "@mui/icons-material";
import { Alert, Box, Button, Card, Divider, Stack, Typography } from "@mui/joy";
import { useState } from "react";
import BankLinkView from "./BankLinkView";
import { FormattedMessage } from "react-intl";

const formatByn = (value: number) => `${value.toFixed(2)} BYN`;

export default function BalanceView({
  balance,
  topUp,
}: {
  balance: BalanceQuery["balance"];
  topUp: EripTopUpQuery["eripTopUp"];
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    await navigator.clipboard.writeText(topUp.fullAccountNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Stack gap={4} pb={8}>
      <Stack gap={1}>
        <Typography level="body-sm" textColor="neutral.400">
          <FormattedMessage defaultMessage="На вашем балансе" />
        </Typography>
        <Typography level="h1">{formatByn(balance.amount)}</Typography>
        {balance.debt > 0 && (
          <Typography level="body-sm" color="danger">
            <FormattedMessage defaultMessage="Задолженность: {value}" values={{ value: formatByn(balance.debt) }} />
          </Typography>
        )}
      </Stack>

      <Divider />

      <Stack gap={2}>
        <Typography level="title-lg">
          <FormattedMessage defaultMessage="Пополнить через ЕРИП" />
        </Typography>
        <Typography level="body-sm" textColor="neutral.500">
          <FormattedMessage defaultMessage="Оплатите любую сумму в приложении своего банка — деньги появятся на балансе автоматически. Номер счёта постоянный, его можно сохранить в банке и платить повторно." />
        </Typography>

        <Card variant="soft">
          <Stack gap={1}>
            <Typography level="body-xs" textColor="neutral.500">
              <FormattedMessage defaultMessage="Номер счёта для оплаты" />
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography level="h3" sx={{ wordBreak: "break-all" }}>
                {topUp.fullAccountNumber}
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                color={copied ? "success" : "neutral"}
                onClick={onCopy}
                startDecorator={copied ? <Done /> : <ContentCopy />}
              >
                {copied ? (
                  <FormattedMessage defaultMessage="Скопировано" />
                ) : (
                  <FormattedMessage defaultMessage="Копировать" />
                )}
              </Button>
            </Stack>
          </Stack>
        </Card>

        {topUp.qrCode && (
          <Stack alignItems="center" gap={1}>
            <Typography level="body-sm" textColor="neutral.500">
              <FormattedMessage defaultMessage="Или отсканируйте QR-код в приложении банка" />
            </Typography>
            <Box
              component="img"
              src={topUp.qrCode}
              alt="QR"
              sx={{ width: 220, height: 220, borderRadius: "sm", bgcolor: "#fff", p: 1 }}
            />
          </Stack>
        )}
      </Stack>

      <Alert variant="outlined" size="sm">
        <Stack gap={0.5}>
          <Typography level="body-sm">
            <FormattedMessage
              defaultMessage="Путь в дереве ЕРИП: Система «Расчёт» (ЕРИП) → по номеру услуги {code} → введите номер счёта {account}."
              values={{ code: topUp.serviceCode, account: topUp.accountNumber }}
            />
          </Typography>
          {topUp.instruction.map((step) => (
            <Typography key={step} level="body-xs" textColor="neutral.500">
              {step}
            </Typography>
          ))}
        </Stack>
      </Alert>

      {topUp.banks.length > 0 && (
        <Stack gap={2}>
          <Typography level="title-md">
            <FormattedMessage defaultMessage="Банки, через которые можно оплатить" />
          </Typography>
          <Typography level="body-xs" textColor="neutral.500">
            <FormattedMessage defaultMessage="Нажмите на свой банк — откроется его приложение с уже заполненным счётом. Сумму нужно будет ввести самой." />
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {topUp.banks.map((bank) => (
              <BankLinkView key={bank.name} bank={bank} />
            ))}
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
